import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { favoritesService } from '../api/supabase';

const Favorites = () => {
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState(new Set());
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();
  const [isAdding, setIsAdding] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      loadFavoriteProducts();
    }

    const handleFavoritesUpdate = () => {
      if (isAuthenticated) {
        loadFavoriteProducts();
      }
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);

    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, [isAuthenticated]);

  const loadFavoriteProducts = async () => {
    if (!isAuthenticated) {
      setFavoriteProducts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await favoritesService.get();
      // Extract products from the favorites structure
      setFavoriteProducts(data?.map(fav => fav.products).filter(Boolean) || []);
    } catch (error) {
      console.error('Failed to load favorite products:', error);
      setFavoriteProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (productId) => {
    try {
      await favoritesService.remove(productId);
      setFavoriteProducts(prev => prev.filter(product => product.id !== productId));
      window.dispatchEvent(new CustomEvent('favoritesUpdated'));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      addToast('请先登录以添加到购物车', 'warning');
      window.location.href = '/login';
      return;
    }

    setIsAdding(prev => ({ ...prev, [product.id]: true }));
    try {
      await addToCart(product.id, 1);
      addToast(`${product.name} 已添加到购物车！`, 'success');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      addToast('添加到购物车失败', 'error');
    } finally {
      setIsAdding(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'CNY',
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (favoriteProducts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Heart size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">暂无收藏</h2>
          <p className="text-gray-600 mb-6">开始添加商品到收藏夹吧！</p>
          <Link to="/products" className="btn-primary">
            浏览商品
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">我的收藏</h1>
          <p className="text-gray-600">
            {favoriteProducts.length} 个收藏商品
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {favoriteProducts.map((product) => (
            <div key={product.id} className="product-card group">
              <div className="relative overflow-hidden bg-gray-100">
                <Link to={`/products/${product.id}`}>
                  {product.image_url && !failedImages.has(product.id) ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={() => setFailedImages(prev => new Set(prev).add(product.id))}
                    />
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gray-200">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  )}
                </Link>

                <button
                  onClick={() => handleToggleFavorite(product.id)}
                  className="absolute top-2 right-2 p-2 bg-red-500 rounded-full shadow-soft opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-600"
                  title="取消收藏"
                >
                  <Heart size={16} className="text-white fill-current" />
                </button>

                {product.stock <= 10 && product.stock > 0 && (
                  <span className="absolute top-2 left-2 badge badge-warning">
                    库存不足
                  </span>
                )}
                {product.stock === 0 && (
                  <span className="absolute top-2 left-2 badge badge-error">
                    缺货
                  </span>
                )}
              </div>

              <div className="p-4">
                <div className="text-xs text-primary-600 font-medium mb-1">
                  {product.category}
                </div>

                <Link to={`/products/${product.id}`}>
                  <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">
                    {product.name}
                  </h3>
                </Link>

                {product.rating && (
                  <div className="flex items-center space-x-1">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className={`${
                            i < Math.floor(product.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-2 mb-3">
                  <span className="text-lg font-bold text-gray-900">
                    {formatPrice(product.price)}
                  </span>
                </div>

                <button
                  onClick={() => handleAddToCart(product)}
                  disabled={isAdding[product.id] || product.stock === 0}
                  className={`w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                    product.stock === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 text-white hover:shadow-medium'
                  }`}
                >
                  {isAdding[product.id] ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>添加中...</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={16} />
                      <span>
                        {product.stock === 0 ? '缺货' : '加入购物车'}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Favorites;